<template>
  <BaseWidget
    title="Inbox"
    :edit-mode="editMode"
    :widget-id="props.widgetId"
    :widget-type="props.widgetType"
    :show-info-icon="true"
    info-tooltip="Your most recent messages from the inbox."
    :info-tooltip-link="{ url: 'https://support.optixapp.com/en/articles/2056121-how-do-i-send-a-message-to-a-user', text: 'Learn about Inbox' }"
    :disable-date-range="true"
    @remove-widget="$emit('remove-widget', props.widgetId)"
  >
    <div
      v-if="loading"
      class="loading-state"
    >
      <SkeletonLoader
        type="widget"
        :show-table="true"
        :compact="focused"
      />
    </div>
    <div
      v-else-if="error"
      class="error-state"
    >
      Error: {{ error.message }}
    </div>
    <div
      v-else
      class="widget-content-wrapper"
      :class="{ 'focused': focused }"
    >
      <MetricValue
        :value="unreadCount.toString()"
        subtitle="Conversations"
        :has-data-table="conversations.length > 0 && !focused"
      >
        <template #comparison>
          <span
            class="unread-text"
            :class="{ 'unread-text--zero': unreadCount === 0 }"
          >Unread</span>
        </template>
        <template
          v-if="conversations.length > 0"
          #subtitle-suffix
        >
          <a
            class="focused-view-all-link"
            href="#"
            @click.prevent="handleViewAllClick"
          >
            View all
          </a>
        </template>
      </MetricValue>
      <div
        v-if="conversations.length > 0"
        class="messages-list"
      >
        <a
          v-for="(conv, index) in displayConversations"
          :key="conv.conversation_id || index"
          class="message-item"
          href="#"
          @click.prevent="handleConversationClick(conv)"
          @mousedown.stop
        >
          <div class="message-avatar-wrapper">
            <span
              v-if="(conv.unread_message_count || 0) > 0"
              class="unread-dot"
              aria-hidden="true"
            />
            <div class="message-avatar">
              <img
                v-if="conv.image && conv.image.url"
                :src="conv.image.url"
                :alt="conv.name"
              >
              <div
                v-else
                class="avatar-placeholder"
              >
                {{ getInitials(conv.name) }}
              </div>
            </div>
          </div>
          <div class="message-details">
            <div class="message-header-row">
              <span class="message-name">{{ truncate(conv.name, 25) }}</span>
              <span class="message-date">{{ formatDate(conv.latest_message?.timestamp) }}</span>
            </div>
            <div class="message-snippet">{{ getMessageSnippet(conv) }}</div>
          </div>
        </a>
      </div>
      <div
        v-else-if="!loading && conversations.length === 0"
        class="no-messages"
      >
        No recent messages
      </div>
    </div>
  </BaseWidget>
</template>

<script setup>
import { computed, onMounted, onUnmounted, watch } from 'vue'
import BaseWidget from './BaseWidget.vue'
import MetricValue from './MetricValue.vue'
import SkeletonLoader from './SkeletonLoader.vue'

import { useGraphQLData } from '../composables/useWidgetData.js'
import { getOrganizationIdFromURL } from '../utils/tokenHandler.js'
import { useDemoMode } from '../composables/useDemoMode.js'
import { useTrialModeGuard } from '../composables/useTrialModeGuard.js'
import { pushConversationPanel, isInOptixIframe } from '../composables/useOptixPanel.js'
import { getDemoConversationsData } from '../services/demoData.js'

const props = defineProps({
  editMode: {
    type: Boolean,
    default: false
  },
  rowSpan: {
    type: Number,
    default: 1
  },
  colSpan: {
    type: Number,
    default: 1
  },
  props: {
    type: Object,
    default: () => ({})
  },
  widgetId: {
    type: String,
    default: null
  },
  sectionId: {
    type: String,
    default: null
  },
  widgetType: {
    type: String,
    default: null
  },
  priority: {
    type: Number,
    default: null
  },
  focused: {
    type: Boolean,
    default: false
  }
})

defineEmits(['update:props', 'remove-widget'])

const organizationId = computed(() => getOrganizationIdFromURL())

const conversationsQuery = `
  query GetConversations(
    $organizationId: ID
    $conversationSequenceFrom: ID
    $order: ConversationOrder
    $search: String
    $accountId: ID
    $includeJoinable: [ConversationType!] = []
    $conversationType: [ConversationType!] = []
    $excludeJoined: [ConversationType!] = []
  ) {
    conversations(
      organization_id: $organizationId
      conversation_sequence_from: $conversationSequenceFrom
      limit: 3
      order: $order
      search: $search
      account_id: $accountId
      conversation_type: $conversationType
      include_joinable: $includeJoinable
      exclude_joined: $excludeJoined
    ) {
      total
      unread_conversation_count
      unread_message_count
      conversation_sequence_up_to
      has_more
      data {
        conversation_id
        name
        latest_message {
          user_id
          message
          plaintext
          timestamp
        }
        image {
          url
        }
        unread_message_count
        conversation_type
        created_timestamp
        users
      }
    }
  }
`

const demoMode = useDemoMode()
const { data, loading, error, cleanup, refresh } = useGraphQLData({
  dataKey: 'conversations',
  query: conversationsQuery,
  variables: computed(() => ({
    organizationId: organizationId.value || undefined,
    order: 'CONVERSATION_SEQUENCE_DESC'
  })),
  operationName: 'GetConversations',
  priority: props.priority ?? 1,
  autoFetch: true,
  getDemoData: () => getDemoConversationsData()
})

// Refresh when trial mode toggles so we show demo vs real data
watch(() => demoMode?.value, (isDemoMode) => {
  if (isDemoMode !== undefined) {
    refresh(true)
  }
}, { immediate: false })

const conversations = computed(() => {
  const items = data.value?.conversations?.data ?? []
  return items.filter((item) => {
    // Hide support chats without a user (no "&" in the name)
    if (item.conversation_type === 'USER_TO_ADMINS') {
      return item.name && item.name.includes('&')
    }
    return true
  })
})

const unreadCount = computed(() => {
  return data.value?.conversations?.unread_conversation_count ?? 0
})

const displayConversations = computed(() => {
  return conversations.value.slice(0, 3)
})

const getInitials = (name) => {
  if (!name) return '?'
  const words = String(name).trim().split(/\s+/)
  if (words.length >= 2) {
    return (words[0][0] + words[words.length - 1][0]).toUpperCase()
  }
  return String(name).charAt(0).toUpperCase()
}

const truncate = (str, maxLen) => {
  if (!str) return ''
  const s = String(str).trim()
  if (s.length <= maxLen) return s
  return s.slice(0, maxLen) + '...'
}

const getMessageSnippet = (conv) => {
  const msg = conv.latest_message
  if (!msg) return 'No message'
  return (msg.plaintext || msg.message || '').trim() || 'No message'
}

const formatDate = (timestamp) => {
  if (!timestamp) return ''
  const date = new Date(timestamp * 1000)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

const getUrlParams = () => {
  const urlParams = new URLSearchParams(window.location.search)
  let clientId = urlParams.get('client_id') || urlParams.get('clientId') || undefined
  let canvasUUID = urlParams.get('canvasUUID') || urlParams.get('canvas_uuid') || urlParams.get('canvasUuid') || ''

  if (!clientId || !canvasUUID) {
    try {
      const referrer = document.referrer
      if (referrer) {
        const referrerUrl = new URL(referrer)
        const referrerParams = new URLSearchParams(referrerUrl.search)
        if (!clientId) clientId = referrerParams.get('client_id') || referrerParams.get('clientId') || undefined
        if (!canvasUUID) canvasUUID = referrerParams.get('canvasUUID') || referrerParams.get('canvas_uuid') || referrerParams.get('canvasUuid') || ''
      }
    } catch (_) {}
  }

  if (!clientId || !canvasUUID) {
    try {
      if (window.parent && window.parent !== window && window.parent.location) {
        const parentUrl = new URL(window.parent.location.href)
        const parentParams = new URLSearchParams(parentUrl.search)
        if (!clientId) clientId = parentParams.get('client_id') || parentParams.get('clientId') || undefined
        if (!canvasUUID) canvasUUID = parentParams.get('canvasUUID') || parentParams.get('canvas_uuid') || parentParams.get('canvasUuid') || ''
      }
    } catch (_) {}
  }

  return { client_id: clientId, canvasUUID }
}

const handleViewAllClick = () => {
  const params = getUrlParams()
  const finalCanvasUUID = params.canvasUUID || 'default-canvas-uuid'
  const navigationUrl = '/inbox'

  const message = {
    command: 'ReplaceUrl',
    payload: {
      client_id: params.client_id || '',
      canvasUUID: finalCanvasUUID,
      url: navigationUrl,
      reload: true
    }
  }

  try {
    const inIframe = window.parent && window.parent !== window
    // Post to parent (iframe) or self (WKWebView — native bridge listens on window)
    const target = inIframe ? window.parent : window
    target.postMessage(message, '*')
    if (inIframe && window.top && window.top !== window && window.top !== window.parent) {
      window.top.postMessage(message, '*')
    }
  } catch (err) {
    console.error('Error navigating to inbox:', err)
  }
}

const { guard } = useTrialModeGuard()
const handleConversationClick = guard((conv) => {
  if (!conv?.conversation_id) return
  // For direct messages, pass account_id (other participant) if we can derive it
  let accountId = null
  const currentUserId = new URLSearchParams(window.location.search).get('current_user_id') || new URLSearchParams(window.location.search).get('userId')
  if (conv.users && Array.isArray(conv.users) && currentUserId) {
    const other = conv.users.find(u => {
      const id = typeof u === 'object' ? u?.user_id ?? u?.id : u
      return id && String(id) !== String(currentUserId)
    })
    accountId = other ? (typeof other === 'object' ? other?.user_id ?? other?.id : other) : null
  }
  pushConversationPanel(conv.conversation_id, accountId)
})

// Refresh when our iframe regains focus (e.g. after closing a conversation panel)
let hasFocusRefreshed = false
const handleFocus = () => {
  if (!hasFocusRefreshed) {
    hasFocusRefreshed = true
    refresh(true)
    // Debounce: allow another refresh after 2s
    setTimeout(() => { hasFocusRefreshed = false }, 2000)
  }
}

onMounted(() => {
  window.addEventListener('focus', handleFocus)
})

onUnmounted(() => {
  window.removeEventListener('focus', handleFocus)
  cleanup()
})
</script>

<style scoped>
.focused-view-all-link {
  font-size: 13px;
  flex-shrink: 0;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
  text-decoration: none;
  transition: color 0.2s ease;
}

.focused-view-all-link:hover {
  color: #2563eb;
}

.widget-content-wrapper {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
}

.widget-content-wrapper :deep(.metric-value) {
  padding-bottom: 12px;
}

.widget-content-wrapper.focused .messages-list {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.messages-list {
  display: flex;
  flex-direction: column;
  gap: 0;
  flex: 1;
}

/* In non-focused mode, don't grow - prevents gap before footer divider */
.widget-content-wrapper:not(.focused) .messages-list {
  flex: 0 0 auto;
}

.message-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 4px;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-radius: 4px;
  margin: 0 -4px;
  min-height: 64px;
  height: 64px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  box-sizing: border-box;
}

.message-item:first-child {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.message-item:last-child {
  border-bottom: none;
}

.message-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.message-avatar-wrapper {
  position: relative;
  flex-shrink: 0;
}

.unread-text {
  font-size: 14px;
  font-weight: 500;
  color: #CB912F;
}

.unread-text--zero {
  color: rgba(0, 0, 0, 0.38);
  font-weight: 400;
}

.unread-dot {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #2563eb;
  border: 2px solid white;
  z-index: 1;
}

.message-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: #f0f0f0;
}

.message-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
  font-size: 14px;
}

.message-details {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.message-header-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}

.message-name {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.message-date {
  font-size: 12px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.6);
  flex-shrink: 0;
}

.message-snippet {
  font-size: 13px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.87);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}

.messages-footer {
  display: flex;
  justify-content: flex-end;
  padding: 12px 0 12px 0;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
}

.view-all-link {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
  text-decoration: none;
  transition: color 0.2s ease;
}

.view-all-link:hover {
  color: #2563eb;
}

.loading-state,
.error-state {
  padding: 20px;
  text-align: center;
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
}

.error-state {
  color: var(--color-red, #dc2626);
}

.no-messages {
  padding: 20px;
  text-align: center;
  color: #999;
  font-size: 14px;
}
</style>
